import type {
    ImageRenameResponse,
    ImportInstancesResponse,
    PatientDetailDto,
} from './connector-next-schemas';
import { parseJsonBody, send } from './saveHttp';

/**
 * Hand-written client for persisting image series to connector-next.
 * HTTP conventions (If-Match, 409/428 mapping, PHI-safe problem parsing)
 * live in `saveHttp.ts`, shared with the other save clients.
 *
 * Upload goes through the standard DICOM ingest endpoint: one multipart
 * request is atomic on the server (staging scope + one transaction, so a
 * failed request leaves nothing behind), and a retried request is
 * idempotent (slices whose SOP Instance UID already exists are skipped).
 */

/**
 * POST /api/dicom/instances — uploads DICOM files as one atomic batch.
 * The request cap (512 MB) lives on the domain port as
 * MAX_UPLOAD_REQUEST_BYTES; the saver refuses larger series up front.
 * The server routes purely by the DICOM PatientID inside the files; the
 * caller is responsible for verifying they belong to the loaded patient.
 *
 * Returns the image-series rows the batch touched (new and
 * already-present alike), so the caller adopts identity from the upload
 * response itself. Rows missing any identity field, and rows of other
 * dataset kinds, are dropped.
 */
export async function postDicomInstances(
    files: readonly Blob[]
): Promise<UploadedSeriesBatch> {
    const form = new FormData();
    files.forEach((file, index) => {
        // Synthetic, PHI-free part names — the server reads everything
        // from the DICOM bytes, never from the filename.
        form.append(
            'files',
            file,
            `slice-${String(index + 1).padStart(4, '0')}.dcm`
        );
    });
    let afterInterruptedAttempt = false;
    // No Content-Type header: the browser must set the multipart boundary.
    // Safe to retry although an unguarded POST: the ingest skips SOP
    // Instance UIDs it already holds, so a replay stores nothing twice.
    // A replay of an upload that landed reports its row as not new, which
    // is why the interruption is passed on to the caller.
    const response = await send(
        '/api/dicom/instances',
        { method: 'POST', body: form },
        undefined,
        {
            retryPolicy: 'transient',
            onRetry: () => {
                afterInterruptedAttempt = true;
            },
        }
    );
    const body = await parseJsonBody<ImportInstancesResponse>(response);
    if (body.datasets == null) {
        // Counts only — never row identities — so a support log can tell
        // an old connector (no per-dataset reporting) from a lost row.
        console.warn(
            `DICOM ingest response carried no datasets field ` +
                `(${files.length} files sent); the caller falls back to ` +
                `the patient detail for identity adoption.`
        );
        return { rows: [], afterInterruptedAttempt };
    }
    const rows: UploadedSeriesRow[] = [];
    for (const dataset of body.datasets) {
        if (dataset.kind !== 'Image3D') {
            continue;
        }
        if (
            !dataset.datasetId ||
            !dataset.seriesInstanceUid ||
            !dataset.version
        ) {
            continue;
        }
        rows.push({
            id: dataset.datasetId,
            seriesInstanceUid: dataset.seriesInstanceUid,
            version: dataset.version,
            seriesDescription: dataset.seriesDescription ?? null,
            // Absent on a connector that predates the flag; false is the
            // safe reading (adoption, never a claimed creation).
            wasNew: dataset.wasNew === true,
        });
    }
    return { rows, afterInterruptedAttempt };
}

/** One image-series row of the patient detail, as needed for adoption. */
export interface ImageSeriesRow {
    readonly id: string;
    readonly seriesInstanceUid: string;
    readonly version: string;
    /** The row's display name, so a caller can tell whether a rename still needs sending. */
    readonly seriesDescription: string | null;
}

/** An upload-response row: the adoption identity plus whether this request created the row. */
export interface UploadedSeriesRow extends ImageSeriesRow {
    readonly wasNew: boolean;
}

/** The rows one upload reported, and whether it needed more than one attempt. */
export interface UploadedSeriesBatch {
    readonly rows: UploadedSeriesRow[];
    /**
     * An earlier attempt failed after possibly storing the series, so a row
     * reported as not new may be this upload's own.
     */
    readonly afterInterruptedAttempt: boolean;
}

/**
 * GET /api/patients/{id} — lists the patient's image series rows. Used
 * when the ingest response carries no matching row (a connector without
 * per-dataset reporting), and to re-read a held ref's row before a
 * version-guarded write. Rows missing any of the identity fields are
 * dropped (same optionality posture as buildConnectorManifest).
 */
export async function getPatientImageSeries(
    patientId: string
): Promise<ImageSeriesRow[]> {
    // Labelled explicitly: a connector patient id is not guaranteed to
    // carry a digit, which is what the default log template masks on.
    const response = await send(
        `/api/patients/${patientId}`,
        { method: 'GET' },
        undefined,
        { label: 'GET /api/patients/{id}' }
    );
    const detail = await parseJsonBody<PatientDetailDto>(response);
    const rows: ImageSeriesRow[] = [];
    for (const study of detail.studies ?? []) {
        for (const series of study.series ?? []) {
            if (series.datasetType !== 'Image3D') {
                continue;
            }
            if (!series.id || !series.seriesInstanceUid || !series.version) {
                continue;
            }
            rows.push({
                id: series.id,
                seriesInstanceUid: series.seriesInstanceUid,
                version: series.version,
                seriesDescription: series.seriesDescription ?? null,
            });
        }
    }
    return rows;
}

/**
 * PATCH /api/images/{id} — renames the series (row display metadata
 * only; the stored slice files are untouched). Version-guarded.
 */
export async function patchImageLabel(
    id: string,
    ifMatch: string,
    seriesDescription: string
): Promise<ImageRenameResponse> {
    const response = await send(
        `/api/images/${id}`,
        {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ seriesDescription }),
        },
        ifMatch
    );
    return parseJsonBody<ImageRenameResponse>(response);
}
